import React, { useState, useEffect } from "react";
import { MdKeyboardArrowLeft } from "react-icons/md";
import { fetchState } from "../../../node_modules/shops-query/src/modules/State/Controller/index.js";
const AddAddressDialogBox = ({ isOpen, onClose, onSave, initialData = null, userId }) => {
  const [formData, setFormData] = useState({
    userId: userId,
    firstName: "",
    phone: "",
    pincode: "",
    street: "",
    town: "",
    city: "",
    state: "",
    country: "India",
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // ✅ Prefill data when editing
  useEffect(() => {
    if (initialData) {
      setFormData({
        userId: userId,
        firstName: initialData.name || "",
        phone: initialData.phone || "",
        pincode: initialData.pincode || "",
        street: initialData.street || "",
        town: initialData.town || "",
        city: initialData.city || "",
        state: initialData.state || "",
        country: initialData.country || "India",
      });
    } else {
      // Reset form when adding new address
      setFormData({
        userId: userId,
        firstName: "",
        phone: "",
        pincode: "",
        street: "",
        town: "",
        city: "",
        state: "",
        country: "India",
      });
    }
    setErrors({});
  }, [initialData, isOpen]);

  if (!isOpen) return null;

  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName?.trim()) {
      newErrors.firstName = "First name is required";
    }

    if (!formData.phone?.trim()) {
      newErrors.phone = "Mobile number is required";
    } else if (!/^\d{10}$/.test(formData.phone)) {
      newErrors.phone = "Please enter a valid 10-digit mobile number";
    }

    if (!formData.pincode?.trim()) {
      newErrors.pincode = "Pincode is required";
    } else if (!/^\d{6}$/.test(formData.pincode)) {
      newErrors.pincode = "Please enter a valid 6-digit pincode";
    }

    if (!formData.street?.trim()) {
      newErrors.street = "Address is required";
    }

    if (!formData.town?.trim()) {
      newErrors.town = "Locality/Town is required";
    }

    if (!formData.city?.trim()) {
      newErrors.city = "City/District is required";
    }

    if (!formData.state?.trim()) {
      newErrors.state = "State is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const fetchLocality = async (pincodeData) => {
    if (!pincodeData || pincodeData.length !== 6) return;
    try {
      const localityData = await fetchState(pincodeData);
      const data = Array.isArray(localityData) ? localityData[0] : localityData;
      if (data) {
        setFormData((prev) => ({
          ...prev,
          town: data.name || prev.town,
          city: data.district || prev.city,
          state: data.state || prev.state,
          country: data.country || "India",
        }));
      }
    } catch (error) {
      console.log("Fetch Locality based on pincode failed", error);
    }
  };

  const handleChange = async (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }

    if (name === "pincode" && value.length === 6) {
      await fetchLocality(value);
    }
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      await onSave(formData, initialData?.id);
      // Modal will be closed by parent component after successful save
    } catch (error) {
      console.log("Error saving address:", error);
      alert("Failed to save address. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setErrors({});
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div className="bg-white w-[90%] max-w-[500px] rounded-2xl shadow-lg p-6 overflow-y-auto max-h-[90vh] font-poppins">
        {/* Header */}
        <div className="flex items-center mb-6">
          <button onClick={handleClose} className="text-2xl mr-3">
            <MdKeyboardArrowLeft />
          </button>
          <h2 className="font-semibold text-[20px] leading-[100%] uppercase text-[#101011]">
            {initialData ? "Edit Address" : "Add New Address"}
          </h2>
        </div>

        {/* Contact Details */}
        <div>
          <p className="font-semibold text-[16px] text-[#101011] mb-2">
            Contact Details
          </p>

          <label className="text-[#424242] text-[14px]">First Name *</label>
          <input
            name="firstName"
            type="text"
            value={formData.firstName}
            onChange={handleChange}
            placeholder="Enter Name"
            className={`w-full border rounded-md p-3 mb-1 placeholder-[#BDBDBD] font-semibold ${errors.firstName ? "border-red-500" : "border-[#DADADA]"
              }`}
          />
          {errors.firstName && (
            <p className="text-red-500 text-xs mb-3">{errors.firstName}</p>
          )}

          <label className="text-[#424242] text-[14px]">Mobile Number *</label>
          <input
            name="phone"
            type="text"
            value={formData.phone}
            onChange={handleChange}
            placeholder="Enter Mobile Number"
            className={`w-full border rounded-md p-3 mb-1 placeholder-[#BDBDBD] font-semibold ${errors.phone ? "border-red-500" : "border-[#DADADA]"
              }`}
          />
          {errors.phone && (
            <p className="text-red-500 text-xs mb-3">{errors.phone}</p>
          )}
        </div>

        {/* Address Section */}
        <div>
          <p className="font-semibold text-[16px] text-[#101011] mb-2">
            Address
          </p>

          <label className="text-[#424242] text-[14px]">Pincode *</label>
          <input
            name="pincode"
            type="text"
            value={formData.pincode}
            onChange={handleChange}
            placeholder="Enter Pincode"
            className={`w-full border rounded-md p-3 mb-1 placeholder-[#BDBDBD] font-semibold ${errors.pincode ? "border-red-500" : "border-[#DADADA]"
              }`}
          />
          {errors.pincode && (
            <p className="text-red-500 text-xs mb-3">{errors.pincode}</p>
          )}

          <label className="text-[#424242] text-[14px]">
            Address (House No, Street, Area) *
          </label>
          <textarea
            name="street"
            value={formData.street}
            onChange={handleChange}
            placeholder="Address"
            rows="3"
            className={`w-full border rounded-md p-3 mb-1 placeholder-[#BDBDBD] font-semibold resize-none flex items-center ${errors.street ? "border-red-500" : "border-[#DADADA]"
              }`}
          />
          {errors.street && (
            <p className="text-red-500 text-xs mb-3">{errors.street}</p>
          )}

          <label className="text-[#424242] text-[14px]">Locality / Town *</label>
          <input
            name="town"
            type="text"
            value={formData.town}
            onChange={handleChange}
            placeholder="Locality / Town"
            className={`w-full border rounded-md p-3 mb-1 placeholder-[#BDBDBD] font-semibold ${errors.town ? "border-red-500" : "border-[#DADADA]"
              }`}
          />
          {errors.town && (
            <p className="text-red-500 text-xs mb-3">{errors.town}</p>
          )}

          <div className="flex gap-4">
            <div className="flex-1">
              <label className="text-[#424242] text-[14px]">
                City / District *
              </label>
              <input
                name="city"
                type="text"
                value={formData.city}
                onChange={handleChange}
                placeholder="City / District"
                className={`w-full border rounded-md p-3 mb-1 placeholder-[#BDBDBD] font-semibold ${errors.city ? "border-red-500" : "border-[#DADADA]"
                  }`}
              />
              {errors.city && (
                <p className="text-red-500 text-xs mb-3">{errors.city}</p>
              )}
            </div>
            <div className="flex-1">
              <label className="text-[#424242] text-[14px]">State *</label>
              <input
                name="state"
                type="text"
                value={formData.state}
                onChange={handleChange}
                placeholder="State"
                className={`w-full border rounded-md p-3 mb-1 placeholder-[#BDBDBD] font-semibold ${errors.state ? "border-red-500" : "border-[#DADADA]"
                  }`}
              />
              {errors.state && (
                <p className="text-red-500 text-xs mb-3">{errors.state}</p>
              )}
            </div>
          </div>
        </div>

        {/* Buttons */}
        <div className="flex justify-between mt-6 gap-4">
          <button
            onClick={handleClose}
            disabled={isSubmitting}
            className="w-1/2 border border-primary-default text-primary-default py-3 rounded-md font-semibold uppercase disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isSubmitting}
            className="w-1/2 bg-primary-default text-white py-3 rounded-md font-semibold uppercase disabled:opacity-50"
          >
            {isSubmitting ? "Saving..." : (initialData ? "Update" : "Save")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddAddressDialogBox;